/**
 * Local bill math helpers for the Site Analysis tab.
 *
 *  - Live recompute of headline insights (total kWh / total cost / blended
 *    rate / peak demand) as the user edits cells — no backend round-trip.
 *  - Chronological ordering of bill months.
 *  - Detect blank / failed-extraction rows so they can be hidden from the
 *    review table.
 *  - Apply Quick-Fix corrections (×10 / ÷10) suggested by the backend
 *    validator.
 */

const MONTH_NAME_TO_NUM = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/** Parse a "MMM-YYYY", "YYYY-MM", "YYYY-MM-DD" etc. bill_month label into a
 *  comparable timestamp (epoch ms). Returns Infinity for unparseable values
 *  so blank rows fall to the bottom of sorted lists. */
export function parseBillMonth(label) {
  if (!label) return Infinity;
  const s = String(label).trim().toLowerCase();
  // "MMM-YYYY" / "MMM YYYY"
  const m1 = s.match(/^([a-z]{3,})[\s\-/]+(\d{4})$/);
  if (m1) {
    const m = MONTH_NAME_TO_NUM[m1[1].slice(0, 3)];
    if (m) return Date.UTC(+m1[2], m - 1, 1);
  }
  // "YYYY-MM" / "YYYY-MM-DD" / "YYYY/MM"
  const m2 = s.match(/^(\d{4})[-/](\d{1,2})/);
  if (m2) return Date.UTC(+m2[1], +m2[2] - 1, 1);
  // "MM/YYYY"
  const m3 = s.match(/^(\d{1,2})[-/](\d{4})$/);
  if (m3) return Date.UTC(+m3[2], +m3[1] - 1, 1);
  return Infinity;
}

/** A bill is considered "blank" if it has no month label AND every numeric
 *  field we surface in the table is zero/empty. */
export function isBlankBill(b) {
  if (!b) return true;
  const hasMonth = !!(b.bill_month && String(b.bill_month).trim());
  const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
  const anyValue = num(b.units_kwh) || num(b.max_demand_kw) || num(b.power_factor)
    || num(b.charges?.energy_charges) || num(b.charges?.net_payable)
    || num(b.charges?.fixed_charges);
  return !hasMonth && !anyValue;
}

/** Sort bills chronologically, blank rows at the bottom. Pure (returns new array). */
export function sortBillsByMonth(bills) {
  return [...(bills || [])]
    .map((b, originalIndex) => ({ b, originalIndex }))
    .sort((a, b) => parseBillMonth(a.b.bill_month) - parseBillMonth(b.b.bill_month));
}

const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);

/** Lightweight insights recompute used to keep the Insights cards reactive
 *  while the user edits the review table. Matches the backend
 *  `compute_analytics` headline math (consumption_cost block). */
export function recomputeHeadlineInsights(bills, contractDemandKw) {
  const rows = (bills || []).filter((b) => !isBlankBill(b));
  if (!rows.length) {
    return {
      months: 0,
      total_units_kwh: 0,
      total_cost_inr: 0,
      blended_rate_inr_per_kwh: 0,
      avg_monthly_bill_inr: 0,
      peak_max_demand_kw: 0,
      contract_demand_kw: contractDemandKw || 0,
      latest_pf: 0,
    };
  }
  const total_units = rows.reduce((s, b) => s + num(b.units_kwh), 0);
  const total_net = rows.reduce((s, b) => s + num(b.charges?.net_payable), 0);
  const peak_max = rows.reduce((m, b) => Math.max(m, num(b.max_demand_kw)), 0);
  const pf_latest = rows[rows.length - 1]?.power_factor;
  return {
    months: rows.length,
    total_units_kwh: total_units,
    total_cost_inr: total_net,
    blended_rate_inr_per_kwh: total_units ? total_net / total_units : 0,
    avg_monthly_bill_inr: rows.length ? total_net / rows.length : 0,
    peak_max_demand_kw: peak_max,
    contract_demand_kw: contractDemandKw || 0,
    latest_pf: num(pf_latest),
  };
}

/** Per-column totals for the table's pinned bottom row. */
export function computeBillTotals(bills) {
  const rows = (bills || []).filter((b) => !isBlankBill(b));
  if (!rows.length) return null;
  const sum = (sel) => rows.reduce((s, b) => s + num(sel(b)), 0);
  const max = (sel) => rows.reduce((m, b) => Math.max(m, num(sel(b))), 0);
  // Power factor: units-weighted average
  const pf_weighted = sum((b) => num(b.power_factor) * num(b.units_kwh))
    / (sum((b) => num(b.units_kwh)) || 1);
  return {
    units_kwh: sum((b) => b.units_kwh),
    max_demand_kw: max((b) => b.max_demand_kw),
    power_factor: pf_weighted,
    'charges.energy_charges': sum((b) => b.charges?.energy_charges),
    'charges.fppas_charges': sum((b) => b.charges?.fppas_charges),
    'charges.fixed_charges': sum((b) => b.charges?.fixed_charges),
    'charges.electricity_duty': sum((b) => b.charges?.electricity_duty),
    'charges.pf_surcharge_incentive': sum((b) => b.charges?.pf_surcharge_incentive),
    'charges.tod_rebate_surcharge': sum((b) => b.charges?.tod_rebate_surcharge),
    'charges.net_payable': sum((b) => b.charges?.net_payable),
    'tod_breakdown.off_peak_kwh': sum((b) => b.tod_breakdown?.off_peak_kwh),
    'tod_breakdown.morning_peak_kwh': sum((b) => b.tod_breakdown?.morning_peak_kwh),
    'tod_breakdown.evening_peak_kwh': sum((b) => b.tod_breakdown?.evening_peak_kwh),
  };
}

/** Apply a Quick-Fix to a single bill (returns a new bill object). */
export function applyQuickFix(bill, fix) {
  if (!fix?.fields?.length) return bill;
  const next = JSON.parse(JSON.stringify(bill || {}));
  for (const path of fix.fields) {
    const parts = path.split('.');
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = cur[parts[i]] ? { ...cur[parts[i]] } : {};
      cur = cur[parts[i]];
    }
    const k = parts[parts.length - 1];
    const v = Number(cur[k]) || 0;
    if (!v) continue;
    if (fix.op === 'divide') cur[k] = v / fix.factor;
    else if (fix.op === 'multiply') cur[k] = v * fix.factor;
  }
  return next;
}

/** Indian-style ₹ formatting with optional compact (L/Cr) form. */
export function formatINRIndian(n, { compact = false, decimals = 0 } = {}) {
  const v = Number(n) || 0;
  if (compact) {
    if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  }
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}`;
}
